// --- HELPER FUNCTIONS ---

export const getYouTubeVideoId = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname === "youtu.be") return urlObj.pathname.slice(1);
    if (urlObj.hostname.includes("youtube.com")) {
      const videoId = urlObj.searchParams.get("v");
      if (videoId) return videoId;
      const pathParts = urlObj.pathname.split("/");
      if (pathParts[1] === "shorts") return pathParts[2];
    }
  } catch (error) {
    console.error("Invalid URL:", url);
  }
  return null;
};

export const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Prefer published date (or returned 'date') over createdAt for display
export const formatArticleDate = (article) => {
  if (!article) return "";
  if (article.publishedDate) return formatDate(article.publishedDate);
  if (article.date) {
    const s = String(article.date).trim();
    const parts = s.split("/");
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10));
      if (!isNaN(dd) && !isNaN(mm) && !isNaN(yyyy)) {
        return new Date(yyyy, mm - 1, dd).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }
    }
    return s;
  }
  return formatDate(article.createdAt);
};

export const escapeHtml = (unsafe) => {
  if (!unsafe) return "";
  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

export const convertStarsToHtml = (text) => {
  if (!text) return "";
  const escaped = escapeHtml(text);
  const replaced = escaped.replace(
    /\*(.+?)\*/g,
    (m, p1) => `<strong>${p1}</strong>`
  );
  return replaced.replace(/\r?\n/g, "<br />");
};

export const truncateHtml = (html, maxChars) => {
  if (!html) return "";
  try {
    const container = document.createElement("div");
    container.innerHTML = html;
    let count = 0;

    const walk = (node) => {
      if (count >= maxChars) {
        node.parentNode && node.parentNode.removeChild(node);
        return false;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue || "";
        const remaining = maxChars - count;
        if (text.length > remaining) {
          node.nodeValue = text.slice(0, remaining) + "...";
          count = maxChars;
        } else {
          count += text.length;
        }
        return true;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const children = Array.from(node.childNodes);
        for (const child of children) {
          const ok = walk(child);
          if (!ok) {
            let next = child.nextSibling;
            while (next) {
              const toRemove = next;
              next = next.nextSibling;
              toRemove.parentNode && toRemove.parentNode.removeChild(toRemove);
            }
            break;
          }
        }
      }
      return true;
    };

    walk(container);
    return container.innerHTML;
  } catch (e) {
    const textOnly = String(html).replace(/<[^>]*>/g, "");
    return escapeHtml(textOnly.length > maxChars ? textOnly.slice(0, maxChars) + "..." : textOnly).replace(/\r?\n/g, "<br />");
  }
};

export const htmlToPlainTextApp = (html) => {
  if (!html) return "";
  try {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = String(html);
    const decoded = textarea.value.replace(/<br\s*\/?>(?=>)?/gi, "\n");
    const div = document.createElement("div");
    div.innerHTML = decoded;
    return div.textContent || div.innerText || "";
  } catch (e) {
    return String(html)
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/<[^>]*>/g, "")
      .replace(/\r?\n/g, "\n");
  }
};

export const getImageUrl = (article, placeholderText = "News", version) => {
  if (article.image?.[0]) {
    let url = article.image[0];
    if (version) {
      url += (url.includes("?") ? "&" : "?") + `v=${version}`;
    }
    return url;
  }
  return article.youtubeUrl
    ? `https://img.youtube.com/vi/${getYouTubeVideoId(
        article.youtubeUrl
      )}/hqdefault.jpg`
    : article.shortVideo
    ? `https://img.youtube.com/vi/${getYouTubeVideoId(
        article.shortVideo
      )}/hqdefault.jpg`
    : `https://placehold.co/800x450/666/fff?text=${placeholderText}`;
};

export const parseCategories = (categories) => {
  if (Array.isArray(categories)) {
    return categories;
  } else if (typeof categories === "string") {
    try {
      return JSON.parse(categories);
    } catch (e) {
      if (categories.includes(",")) {
        return categories.split(",").map((cat) => cat.trim());
      } else {
        return [categories];
      }
    }
  }
  return [];
};
